import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Printer, BluetoothConnected, Check, Warning, ArrowClockwise, WifiHigh, WifiSlash, DeviceMobile } from '@phosphor-icons/react';
import JsBarcode from 'jsbarcode';
import { templatesApi } from '../services/api';
import { buildTicketPrintHTML } from '../utils/ticketRenderer';

// ============================================================
// Constants
// ============================================================

const COLS = 32;
const SEP = '-'.repeat(COLS);
const CONNECTION_TIMEOUT = 15000;
const PRINTER_SERVICE_UUIDS = [
  '000018f0-0000-1000-8000-00805f9b34fb',
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2'
];

// ============================================================
// Format helpers
// ============================================================

function fmtDate(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString('es-CL', {
    timeZone: 'America/Santiago',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
}

function fmtMoney(amount) {
  if (amount === null || amount === undefined) return '$0';
  const num = Math.round(Number(amount));
  return '$' + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function fmtDuration(totalMinutes) {
  if (!totalMinutes || totalMinutes < 0) return '0 min';
  const m = Math.round(totalMinutes);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${(m % 60).toString().padStart(2, '0')} min`;
}

function padRow(label, value, width = COLS) {
  const l = label.substring(0, width - value.length - 1);
  const gap = width - l.length - value.length;
  return l + ' '.repeat(Math.max(1, gap)) + value;
}

function centerText(text, width = COLS) {
  const t = text.substring(0, width);
  return ' '.repeat(Math.max(0, Math.floor((width - t.length) / 2))) + t;
}

// ============================================================
// Plain text ticket (for preview + fallback print)
// ============================================================

function buildPlainTextTicket(td, settings, isEntry) {
  const lines = [];
  const bn = settings?.business_name || 'ESTACIONAMIENTO';
  const hdr = settings?.ticket_header || '';
  const ftr = settings?.ticket_footer || '';

  lines.push(centerText(bn));
  if (hdr) lines.push(centerText(hdr));
  lines.push(SEP);
  lines.push('');
  lines.push('TICKET: ' + (td.ticket_id || ''));
  lines.push('');
  lines.push('TIPO:    ' + (td.vehicle_type || ''));
  lines.push(SEP);
  lines.push('FECHA ENTRADA:');
  lines.push('  ' + fmtDate(td.entry_time));

  if (!isEntry) {
    lines.push('FECHA SALIDA:');
    lines.push('  ' + fmtDate(td.exit_time));
    lines.push('TIEMPO TOTAL:');
    lines.push('  ' + fmtDuration(td.total_minutes));
    lines.push(SEP);
    if (td.breakdown && td.breakdown.length > 0) {
      lines.push('DETALLE');
      for (const item of td.breakdown) {
        lines.push(padRow(item.description || '', fmtMoney(item.amount)));
      }
    }
    const ep = td.extras_paid_separately || 0;
    if (ep > 0) lines.push('*Cobrado aparte: ' + fmtMoney(ep));
    lines.push(SEP);
    lines.push('');
    lines.push(padRow('  TOTAL', fmtMoney(td.total_price)));
    lines.push('');
  }

  lines.push(SEP);
  if (ftr) lines.push(centerText(ftr));
  lines.push('');
  lines.push(centerText(td.ticket_id || ''));
  return lines.join('\n');
}

// ============================================================
// ESC/POS byte builder with double-height for key fields
// ============================================================

class ESCPOSBuilder {
  constructor() { this.bytes = []; }
  raw(...vals) { for (const v of vals) this.bytes.push(v & 0xFF); return this; }
  text(str) {
    if (!str) return this;
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      this.bytes.push(c <= 0xFF ? c : 0x3F);
    }
    return this;
  }
  lf() { return this.raw(0x0A); }
  init() { return this.raw(0x1B, 0x40); }
  cp1252() { return this.raw(0x1B, 0x74, 0x10); }
  left() { return this.raw(0x1B, 0x61, 0x00); }
  center() { return this.raw(0x1B, 0x61, 0x01); }
  bold(on) { return this.raw(0x1B, 0x45, on ? 0x01 : 0x00); }
  normal() { return this.raw(0x1D, 0x21, 0x00); } // GS ! 0x00
  doubleH() { return this.raw(0x1D, 0x21, 0x01); } // GS ! 0x01 — double height only (SAFE)
  sep() { return this.text(SEP).lf(); }
  line(s) { return this.text((s || '').substring(0, COLS)).lf(); }
  row(label, value) { return this.text(padRow(label, value)).lf(); }

  // GS k — Print barcode (Code128, subset B)
  barcode128(data) {
    // Set barcode height = 60 dots
    this.raw(0x1D, 0x68, 0x3C);
    // Set barcode width = 2
    this.raw(0x1D, 0x77, 0x02);
    // Print HRI below barcode
    this.raw(0x1D, 0x48, 0x02);
    // GS k 73 — Code128
    this.raw(0x1D, 0x6B, 0x49);
    // Length + data
    const bytes = [];
    // Start with Code B subset
    bytes.push(0x7B, 0x42); // {B
    for (let i = 0; i < data.length; i++) {
      bytes.push(data.charCodeAt(i) & 0x7F);
    }
    this.raw(bytes.length);
    for (const b of bytes) this.raw(b);
    return this.lf();
  }

  // GS ( k — Print QR code
  qrCode(data) {
    const d = [];
    for (let i = 0; i < data.length; i++) d.push(data.charCodeAt(i) & 0xFF);
    const len = d.length + 3;
    // QR model 2
    this.raw(0x1D, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00);
    // QR size 4
    this.raw(0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, 0x04);
    // QR error correction L
    this.raw(0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, 0x30);
    // Store data
    this.raw(0x1D, 0x28, 0x6B, len & 0xFF, (len >> 8) & 0xFF, 0x31, 0x50, 0x30);
    for (const b of d) this.raw(b);
    // Print QR
    this.raw(0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30);
    return this.lf();
  }

  feedCut(n = 4) {
    for (let i = 0; i < n; i++) this.lf();
    return this.raw(0x1D, 0x56, 0x00);
  }
  build() { return new Uint8Array(this.bytes); }
}

function buildTicketBytes(td, settings, isEntry) {
  const b = new ESCPOSBuilder();
  b.init().cp1252().normal();

  // Header
  b.center().bold(true).line(settings?.business_name || 'ESTACIONAMIENTO').bold(false);
  if (settings?.ticket_header) b.line(settings.ticket_header);
  b.sep();

  // Ticket ID — normal size
  b.left().line('TICKET: ' + (td.ticket_id || ''));
  b.lf();

  // PATENTE — DOUBLE HEIGHT (key field)
  b.bold(true).doubleH();
  b.line('PATENTE: ' + (td.plate_number || ''));
  b.normal().lf();

  // ESPACIO — DOUBLE HEIGHT (key field)
  b.bold(true).doubleH();
  b.line('ESPACIO: ' + (td.space_label || ''));
  b.normal().bold(false).lf();

  b.line('TIPO:    ' + (td.vehicle_type || ''));
  b.sep();

  // Timestamps
  b.line('FECHA ENTRADA:');
  b.line('  ' + fmtDate(td.entry_time));

  if (!isEntry) {
    b.line('FECHA SALIDA:');
    b.line('  ' + fmtDate(td.exit_time));
    b.line('TIEMPO TOTAL:');
    b.line('  ' + fmtDuration(td.total_minutes));
    b.sep();

    if (td.breakdown && td.breakdown.length > 0) {
      b.line('DETALLE');
      for (const item of td.breakdown) {
        b.row(item.description || '', fmtMoney(item.amount));
      }
    }
    const ep = td.extras_paid_separately || 0;
    if (ep > 0) b.line('*Cobrado aparte: ' + fmtMoney(ep));
    b.sep();

    // TOTAL — DOUBLE HEIGHT BOLD (key field)
    b.center().bold(true).doubleH();
    b.row('TOTAL', fmtMoney(td.total_price));
    b.normal().bold(false);
  }

  b.sep();
  b.center();
  if (settings?.ticket_footer) b.line(settings.ticket_footer);
  b.lf();

  // Barcode (try Code128, then QR fallback)
  const ticketId = td.ticket_id || '';
  if (ticketId) {
    try {
      b.barcode128(ticketId);
    } catch {
      try { b.qrCode(ticketId); } catch { b.line(ticketId); }
    }
  }

  b.feedCut(4);
  return b.build();
}

// ============================================================
// Validate ticket
// ============================================================

function validateTicket(td, isEntry) {
  const errors = [];
  if (!td.ticket_id) errors.push('Falta ticket ID');
  if (!td.entry_time) errors.push('Falta fecha de entrada');
  if (!td.plate_number) errors.push('Falta patente');
  if (!isEntry) {
    if (!td.exit_time) errors.push('Falta fecha de salida');
    if (td.exit_time && td.entry_time && new Date(td.exit_time) <= new Date(td.entry_time))
      errors.push('Salida anterior a entrada');
    if (td.total_price === undefined || td.total_price === null) errors.push('Falta total');
  }
  return errors;
}

function withTimeout(promise, ms, msg) {
  return Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error(msg)), ms))]);
}

// ============================================================
// Component
// ============================================================

export default function ReceiptPreview({ data, settings, onClose }) {
  const [printing, setPrinting] = useState(false);
  const [printStatus, setPrintStatus] = useState(null);
  const [errorDetail, setErrorDetail] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const barcodeRef = useRef(null);
  const printIframeRef = useRef(null);
  const btDeviceRef = useRef(null);
  const btCharRef = useRef(null);
  const btServerRef = useRef(null);
  const [template, setTemplate] = useState(null); // active ticket template (entry|exit)

  const ticketData = data.data;
  const isEntry = data.type === 'entry';
  const plainTicket = buildPlainTextTicket(ticketData, settings, isEntry);
  const deviceMode = settings?.device_mode || 'desktop';

  useEffect(() => {
    const errs = validateTicket(ticketData, isEntry);
    setValidationErrors(errs);
  }, [ticketData, isEntry]);

  useEffect(() => {
    console.log('[TICKET DEBUG]', data.type, ticketData.entry_time, ticketData.exit_time, ticketData.total_price);
  }, [data.type, ticketData]);

  useEffect(() => {
    let cancelled = false;
    templatesApi.get(isEntry ? 'entry' : 'exit')
      .then((res) => { if (!cancelled) setTemplate(res.data); })
      .catch(() => {}); // Fallback to legacy layout if load fails
    return () => { cancelled = true; };
  }, [isEntry]);

  useEffect(() => {
    if (barcodeRef.current && ticketData.ticket_id) {
      try {
        JsBarcode(barcodeRef.current, ticketData.ticket_id, {
          format: 'CODE128', width: 2, height: 50,
          displayValue: true, fontSize: 12, margin: 10
        });
      } catch (e) { console.error('Barcode error:', e); }
    }
  }, [ticketData.ticket_id]);

  const isConnected = useCallback(() => {
    return btDeviceRef.current?.gatt?.connected && btCharRef.current;
  }, []);

  const findWriteCharacteristic = async (server) => {
    for (const uuid of PRINTER_SERVICE_UUIDS) {
      try {
        const svc = await server.getPrimaryService(uuid);
        const chars = await svc.getCharacteristics();
        const wc = chars.find(c => c.properties.write || c.properties.writeWithoutResponse);
        if (wc) return wc;
      } catch {}
    }
    return null;
  };

  const sendBytes = async (writeChar, bytes) => {
    const chunkSize = 100;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.slice(i, Math.min(i + chunkSize, bytes.length));
      if (writeChar.properties.writeWithoutResponse) {
        await writeChar.writeValueWithoutResponse(chunk);
      } else {
        await writeChar.writeValue(chunk);
      }
      await new Promise(r => setTimeout(r, 50));
    }
  };

  const printViaBluetooth = async () => {
    if (validationErrors.length > 0) { setErrorDetail('Datos invalidos'); setPrintStatus('error'); return; }
    setPrinting(true); setErrorDetail(''); setPrintStatus('connecting');

    try {
      if (!navigator.bluetooth) throw { step: 'api', message: 'Web Bluetooth no disponible' };

      let writeChar = btCharRef.current;
      if (!isConnected()) {
        let btDevice;
        try {
          btDevice = await withTimeout(navigator.bluetooth.requestDevice({
            filters: [
              { services: ['000018f0-0000-1000-8000-00805f9b34fb'] },
              { namePrefix: 'Printer' }, { namePrefix: 'POS' }, { namePrefix: 'BT' },
              { namePrefix: 'MPT' }, { namePrefix: 'RPP' }, { namePrefix: 'PT-' },
              { namePrefix: 'Thermal' }, { namePrefix: 'BlueTooth' }
            ],
            optionalServices: PRINTER_SERVICE_UUIDS
          }), 30000, 'Tiempo agotado');
        } catch (e) {
          if (e.name === 'NotFoundError' || e.message?.includes('cancelled'))
            throw { step: 'select', message: 'No se selecciono dispositivo' };
          try {
            btDevice = await withTimeout(navigator.bluetooth.requestDevice({
              acceptAllDevices: true, optionalServices: PRINTER_SERVICE_UUIDS
            }), 30000, 'Tiempo agotado');
          } catch (e2) {
            throw { step: 'select', message: e2.message || 'Error seleccion' };
          }
        }
        if (!btDevice) throw { step: 'select', message: 'Sin dispositivo' };
        btDeviceRef.current = btDevice;

        const server = await withTimeout(btDevice.gatt.connect(), CONNECTION_TIMEOUT, 'Timeout conexion');
        btServerRef.current = server;
        writeChar = await findWriteCharacteristic(server);
        if (!writeChar) throw { step: 'service', message: 'Servicio de impresion no encontrado' };
        btCharRef.current = writeChar;
      }

      setPrintStatus('sending');
      const bytes = buildTicketBytes(ticketData, settings, isEntry);
      try { await sendBytes(writeChar, bytes); }
      catch (e) { btCharRef.current = null; btServerRef.current = null; throw { step: 'write', message: e.message }; }

      setPrintStatus('success');
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      const detail = error.step ? error.message :
        error.name === 'NotFoundError' ? 'Sin dispositivo' :
        error.name === 'SecurityError' ? 'Permiso denegado' :
        error.message || 'Error desconocido';
      setErrorDetail(detail); setPrintStatus('error');
    } finally { setPrinting(false); }
  };

  // ── EXACT printFallback core flow from ee77233 (window.open primary, iframe popup-block fallback).
  // Safe additions (NOT structural changes):
  //   • CSS-styled hero blocks for PATENTE / ESPACIO / TOTAL added around the existing <pre>plainTicket</pre>
  //   • Pre-rendered barcode SVG (taken from on-screen <svg ref=barcodeRef>) embedded inline — NO external CDN
  //   • Payment method + cash change line (if present)
  // The plain <pre> ticket remains intact as fallback for printer-safe alignment.
  const printFallback = () => {
    if (validationErrors.length > 0) {
      setErrorDetail('Ticket con datos invalidos');
      setPrintStatus('error');
      return;
    }

    // Serialize the already-rendered barcode SVG (locally generated by JsBarcode on screen).
    // No CDN, no remote script in the print window.
    let barcodeMarkup = '';
    try {
      if (barcodeRef.current && ticketData.ticket_id) {
        const svgClone = barcodeRef.current.cloneNode(true);
        svgClone.removeAttribute('class');
        svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        barcodeMarkup = svgClone.outerHTML;
      }
    } catch { barcodeMarkup = ''; }

    // Template-driven body (falls back to legacy layout if template not yet loaded).
    // NOTE: Only the HTML CONTENT is template-driven. The print DELIVERY MECHANISM below
    // (window.open primary → iframe fallback) is UNCHANGED from the restored ee77233 flow.
    const printHTML = template
      ? buildTicketPrintHTML(template, ticketData, barcodeMarkup)
      : buildLegacyPrintHTML(ticketData, plainTicket, barcodeMarkup, isEntry);

    // Method 1: Try window.open (works on most browsers)
    const pw = window.open('', '_blank');
    if (pw) {
      pw.document.write(printHTML);
      pw.document.close();
      return;
    }

    // Method 2: If popup blocked (iOS Safari), use hidden iframe
    try {
      // Remove any previous print iframe
      if (printIframeRef.current) {
        document.body.removeChild(printIframeRef.current);
      }

      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:80mm;height:0;border:none;';
      document.body.appendChild(iframe);
      printIframeRef.current = iframe;

      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(printHTML);
      doc.close();

      // Wait for content to render, then print
      setTimeout(() => {
        try {
          iframe.contentWindow.print();
        } catch (e) {
          console.error('[PRINT] iframe print failed:', e);
        }
        // Cleanup after delay
        setTimeout(() => {
          if (printIframeRef.current) {
            document.body.removeChild(printIframeRef.current);
            printIframeRef.current = null;
          }
        }, 3000);
      }, 600);
    } catch (e) {
      console.error('[PRINT] Fallback print failed:', e);
      setErrorDetail('No se pudo abrir ventana de impresion');
      setPrintStatus('error');
    }
  };

  const handleRetry = () => {
    btDeviceRef.current = null; btCharRef.current = null; btServerRef.current = null;
    setErrorDetail(''); setPrintStatus(null);
  };

  useEffect(() => {
    return () => { if (printIframeRef.current) { try { document.body.removeChild(printIframeRef.current); } catch {} } };
  }, []);

  const ep = ticketData.extras_paid_separately || 0;
  const btSupported = typeof navigator !== 'undefined' && !!navigator.bluetooth;
  const connected = isConnected();

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" data-testid="receipt-modal">
      <div className="bg-[#141414] rounded-xl w-full max-w-md border border-[#27272A]">
        <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
          <h2 className="text-xl font-heading text-white">
            {isEntry ? 'Ticket de Entrada' : 'Ticket de Salida'}
          </h2>
          <div className="flex items-center gap-2">
            {deviceMode === 'pos_terminal' && (
              <span className="px-2 py-1 bg-[#1e3a5f] text-[#60A5FA] text-xs rounded" data-testid="pos-mode-badge">POS</span>
            )}
            <button onClick={onClose} className="p-2 hover:bg-[#27272A] rounded-lg" data-testid="receipt-close">
              <X className="w-6 h-6 text-[#A1A1AA]" weight="bold" />
            </button>
          </div>
        </div>

        {validationErrors.length > 0 && (
          <div className="mx-4 mt-4 p-3 bg-[#450A0A] border border-[#DC2626] text-[#FCA5A5] rounded-lg text-sm" data-testid="ticket-validation-error">
            <p className="font-bold mb-1">Error:</p>
            {validationErrors.map((e, i) => <p key={i}>- {e}</p>)}
          </div>
        )}

        {/* Receipt Preview */}
        <div className="p-4">
          <div className="bg-white rounded-lg p-4 overflow-x-auto" data-testid="receipt-preview">
            {/* Hero header — large PATENTE & ESPACIO (single source, also rendered in printed HTML) */}
            <div className="text-center text-black mb-2" style={{ fontFamily: "'Arial Black','Helvetica',sans-serif" }}>
              <div style={{ fontSize: '10px', letterSpacing: '2px' }}>PATENTE</div>
              <div style={{ fontSize: '24px', fontWeight: 900, letterSpacing: '2px', borderBottom: '2px solid #000', padding: '2px 0' }}
                data-testid="preview-plate">
                {ticketData.plate_number}
              </div>
              <div style={{ fontSize: '10px', letterSpacing: '2px', marginTop: '4px' }}>ESPACIO</div>
              <div style={{ fontSize: '24px', fontWeight: 900, letterSpacing: '2px', borderBottom: '2px solid #000', padding: '2px 0' }}
                data-testid="preview-space">
                {ticketData.space_label}
              </div>
            </div>
            <pre className="text-black font-mono text-xs leading-relaxed whitespace-pre m-0"
                 style={{ fontFamily: "'Courier New', Courier, monospace", fontSize: '11px' }}>
{plainTicket}
            </pre>
            <div className="flex justify-center mt-2">
              <svg ref={barcodeRef}></svg>
            </div>
          </div>
        </div>

        {/* Status */}
        {printStatus && (
          <div className={`mx-4 mb-3 p-3 rounded-lg ${
            printStatus === 'success' ? 'bg-[#052E16] text-[#4ADE80]' :
            printStatus === 'error' ? 'bg-[#450A0A] text-[#FCA5A5]' :
            'bg-[#1C1C1E] text-[#A1A1AA]'
          }`} data-testid="print-status">
            <div className="flex items-center gap-2">
              {printStatus === 'success' && <Check className="w-5 h-5" weight="bold" />}
              {printStatus === 'error' && <Warning className="w-5 h-5" weight="bold" />}
              {(printStatus === 'connecting' || printStatus === 'sending') && <BluetoothConnected className="w-5 h-5 animate-pulse" weight="bold" />}
              <div className="flex-1">
                <span className="font-medium">
                  {printStatus === 'success' && 'Impreso'}
                  {printStatus === 'connecting' && 'Conectando...'}
                  {printStatus === 'sending' && 'Enviando...'}
                  {printStatus === 'error' && 'Error'}
                </span>
                {printStatus === 'error' && errorDetail && <p className="text-xs mt-1 opacity-80" data-testid="print-error-detail">{errorDetail}</p>}
              </div>
            </div>
            {printStatus === 'error' && (
              <div className="mt-3 flex gap-2">
                <button onClick={handleRetry} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#27272A] hover:bg-[#3f3f46] rounded-lg text-sm text-white" data-testid="print-retry-btn">
                  <ArrowClockwise className="w-4 h-4" weight="bold" /> Reintentar
                </button>
                <button onClick={printFallback} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#27272A] hover:bg-[#3f3f46] rounded-lg text-sm text-white" data-testid="print-fallback-from-error-btn">
                  <Printer className="w-4 h-4" weight="bold" /> Alternativo
                </button>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="p-4 border-t border-[#27272A] space-y-2">
          {deviceMode === 'pos_terminal' ? (
            <>
              <button onClick={printFallback} disabled={validationErrors.length > 0}
                className="w-full btn-success text-lg disabled:opacity-50" data-testid="print-pos-btn">
                <span className="flex items-center justify-center gap-2">
                  <DeviceMobile className="w-5 h-5" weight="bold" />
                  Imprimir POS
                </span>
              </button>
              {btSupported && (
                <button onClick={printViaBluetooth} disabled={printing || validationErrors.length > 0}
                  className="w-full btn-secondary disabled:opacity-50" data-testid="print-bluetooth-btn">
                  <span className="flex items-center justify-center gap-2">
                    <BluetoothConnected className="w-5 h-5" weight="bold" />
                    Bluetooth
                  </span>
                </button>
              )}
              <button onClick={printFallback} disabled={validationErrors.length > 0}
                className="w-full btn-secondary disabled:opacity-50 text-sm" data-testid="print-fallback-btn">
                <span className="flex items-center justify-center gap-2">
                  <Printer className="w-4 h-4" weight="bold" />
                  Alternativo
                </span>
              </button>
            </>
          ) : (
            <>
              {btSupported ? (
                <button onClick={printViaBluetooth} disabled={printing || validationErrors.length > 0}
                  className="w-full btn-success text-lg disabled:opacity-50" data-testid="print-bluetooth-btn">
                  <span className="flex items-center justify-center gap-2">
                    <BluetoothConnected className="w-5 h-5" weight="bold" />
                    {printing ? (printStatus === 'sending' ? 'Enviando...' : 'Conectando...') : (connected ? 'Reimprimir' : 'Imprimir (Bluetooth)')}
                  </span>
                </button>
              ) : (
                <div className="w-full p-3 bg-[#1C1C1E] border border-[#27272A] rounded-lg text-center" data-testid="bt-not-supported">
                  <div className="flex items-center justify-center gap-2 text-[#FCD34D] mb-1">
                    <WifiSlash className="w-5 h-5" weight="bold" />
                    <span className="font-medium text-sm">Bluetooth no disponible</span>
                  </div>
                </div>
              )}
              <button onClick={printFallback} disabled={validationErrors.length > 0}
                className="w-full btn-secondary disabled:opacity-50" data-testid="print-fallback-btn">
                <span className="flex items-center justify-center gap-2">
                  <Printer className="w-5 h-5" weight="bold" />
                  Imprimir (Alternativo)
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Legacy fallback print HTML (used only if the ticket template fails to load).
// This preserves the exact ee77233 + safe-hero layout as a safety net.
// The active template from /api/ticket-templates/{kind} is the primary path.
// ============================================================
function buildLegacyPrintHTML(td, plainTicket, barcodeMarkup, isEntry) {
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]));
  const fmt = (n) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n || 0).replace(/\s/g, '');
  const heroHeader = `<div style="text-align:center;font-family:'Arial Black',sans-serif;margin:4px 0">
    <div style="font-size:11px;letter-spacing:2px">PATENTE</div>
    <div style="font-size:26px;font-weight:900;letter-spacing:2px;border-bottom:2px solid #000;padding:2px 0">${esc(td.plate_number)}</div>
    <div style="font-size:11px;letter-spacing:2px;margin-top:4px">ESPACIO</div>
    <div style="font-size:26px;font-weight:900;letter-spacing:2px;border-bottom:2px solid #000;padding:2px 0">${esc(td.space_label)}</div>
  </div>`;
  const totalBlock = !isEntry
    ? `<div style="text-align:center;font-family:'Arial Black',sans-serif;margin-top:8px;padding-top:4px;border-top:2px solid #000">
        <div style="font-size:12px;letter-spacing:3px">TOTAL</div>
        <div style="font-size:30px;font-weight:900">${esc(fmt(td.total_price))}</div>
      </div>` : '';
  const barcodeBlock = `<div style="text-align:center;margin-top:8px;padding-top:4px;border-top:1px dashed #000">
    ${barcodeMarkup || ''}
    <div style="font-family:'Courier New',monospace;font-size:12px;margin-top:2px">${esc(td.ticket_id)}</div>
  </div>`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ticket ${esc(td.ticket_id || '')}</title>
<style>
  @page{size:80mm auto;margin:3mm}
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:100%;background:#fff;color:#000}
  .ticket{font-family:'Courier New',Courier,monospace;font-size:12px;line-height:1.4;white-space:pre;max-width:300px;margin:0 auto;padding:8px 0}
  @media print{@page{size:80mm auto;margin:2mm}html,body{width:80mm}.ticket{max-width:none;width:100%;font-size:11px;padding:0}.no-print{display:none!important}}
  .print-btn{display:block;margin:16px auto;padding:12px 32px;font-size:16px;font-family:sans-serif;background:#16A34A;color:#fff;border:none;border-radius:8px;cursor:pointer}
</style></head><body>
${heroHeader}
<pre class="ticket">${plainTicket}</pre>
${totalBlock}
${barcodeBlock}
<button class="print-btn no-print" onclick="window.print()">Imprimir</button>
<script>setTimeout(function(){window.print()},500);</script>
</body></html>`;
}

// ============================================================
// (buildPrintHTML removed — replaced by inline ee77233 template in printFallback)
// ============================================================
